import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';

import { FormField } from 'site-modules/shared/components/form-field/form-field';
import { ProfileFormErrors } from 'site-modules/shared/components/profile/profile-form-errors/profile-form-errors';
import {
  completeAuthAction,
  progressAuthAction,
  successAuthAction,
} from 'client/engagement-handlers/profile-engagement-handler/profile-engagement-handler';
import {
  getCurrentUser,
  signIn,
  signUp,
  upgradeByEmailPassword,
} from 'site-modules/shared/components/profile/firebase-auth';
import { sameFirebaseSession } from 'site-modules/shared/components/profile/idm/idm-utils';
import { formValidation } from 'site-modules/shared/components/form-validation/form-validation';
import { firebaseValidation } from 'site-modules/shared/components/profile/firebase-validation';
import {
  validators,
  PASSWORD_MAX_LENGTH,
  SIGN_EMAIL_ERROR,
} from 'site-modules/shared/components/profile/smart-sign/helper';
import { Link } from 'site-modules/shared/components/link/link';
import { ReCaptchaButtonAsync } from 'site-modules/shared/components/profile/recaptcha-button/recaptcha-button-async';

export function SignPasswordFormUI({
  profileEmail: email,
  isVerifyEmail,
  fieldRef,
  fields,
  fieldsOrder,
  validate,
  validationErrors = {},
  parseFirebaseError,
  firebaseError = null,
  isAnonymousUser,
  dataIdm,
  createOrUpdateIdmData,
  updateIdmDataOnLogin,
  closeAuthenticatedModal,
  toggleForgotPassword,
  signCompleted,
  backAction,
  isSignInAction = false,
  disableRecaptcha = false,
}) {
  const [isSpinnerActive, setSpinnerActive] = useState(false);
  const useSignInAction = isSignInAction || !!firebaseError;

  function handleSubmitForm(event) {
    event.preventDefault();
  }

  async function handleSignIn(token, skip) {
    completeAuthAction('SIGN_IN', {});
    if (!validate()) {
      return;
    }
    setSpinnerActive(true);
    const password = fields[!isSignInAction && !useSignInAction ? 'passwordSignUp' : 'passwordSignIn'].value();
    const profile = { identifiers: { email: [email] } };

    getCurrentUser()
      .then(user => {
        const aid = sameFirebaseSession(profile, dataIdm, user?.uid);
        return signIn({ email, password, token, skip, disableRecaptcha })
          .then(() => updateIdmDataOnLogin(profile, aid))
          .then(() => {
            successAuthAction('SIGN_IN');
            signCompleted();
          });
      })
      .catch(error => {
        setSpinnerActive(false);
        parseFirebaseError(error);
      });
  }

  async function handleSignUp(token) {
    progressAuthAction('SIGN_UP');
    if (!validate()) {
      return;
    }

    const profile = { identifiers: { email: [email] } };
    const password = fields.passwordSignUp.value();
    setSpinnerActive(true);
    // TODO Since password is optional, if there isn't a password we will stop from creating and close modal.
    if (!password) {
      try {
        await createOrUpdateIdmData({ idmDataToSave: profile });
        closeAuthenticatedModal();
      } catch (error) {
        setSpinnerActive(false);
        parseFirebaseError(error);
      }
      return;
    }

    const signUpProcess =
      isAnonymousUser && sameFirebaseSession(profile, dataIdm, true)
        ? uid =>
            upgradeByEmailPassword({ email, password, token, disableRecaptcha }).then(() =>
              updateIdmDataOnLogin(profile, uid)
            )
        : () =>
            // from /insider/ page, if user exists then call handleSignIn action
            signUp({ email, password, token, disableRecaptcha }).then(() => updateIdmDataOnLogin(profile, null, true));

    getCurrentUser()
      .then(user => signUpProcess(user?.uid))
      .then(() => {
        successAuthAction('SIGN_UP');
        signCompleted();
      })
      .catch(error => {
        if ([error.code, error.message].includes(SIGN_EMAIL_ERROR)) {
          handleSignIn(token, true);
        } else {
          setSpinnerActive(false);
          parseFirebaseError(error);
        }
      });
  }

  const handleSignAction = useSignInAction ? handleSignIn : handleSignUp;
  const formFieldProps = useSignInAction
    ? {
        className: 'mb-0',
        name: 'passwordSignIn',
        'aria-required': true,
        'aria-invalid': 'password' in validationErrors,
        'aria-describedby': 'profile-form-errors',
      }
    : { name: 'passwordSignUp', isOptional: true };

  const btnSignUpText = isSpinnerActive ? 'Submitting' : 'Submit';
  const btnSignInText = isSpinnerActive ? 'Signing in' : 'Sign in';
  const btnText = useSignInAction ? btnSignInText : btnSignUpText;

  return (
    <form id="smart-sign-password-form" noValidate onSubmit={handleSubmitForm}>
      {!isVerifyEmail && (
        <p className="text-gray-darker">{isSignInAction ? 'Please sign into your account' : 'Enter your password'}.</p>
      )}
      <FormField
        maxLength={PASSWORD_MAX_LENGTH}
        id="smart-sign-password"
        type="password"
        label="Password"
        ref={field => fieldRef(formFieldProps.name, field)}
        isValid={!(formFieldProps.name in validationErrors)}
        onBlur={validate}
        data-tracking-id="provide_password"
        data-tracking-ignore-value
        data-no-refresh
        inputClassName="inspectletIgnore"
        labelClassName="medium mb-0_25"
        {...formFieldProps}
      />
      <div className="mt-0_25 mb-0_5">
        <Link
          onClick={toggleForgotPassword}
          to="forgot-password"
          data-tracking-id="recover_password"
          data-no-refresh
          className="open-forgot-password small text-primary-darker"
          rel="nofollow"
        >
          <u>Forgot password?</u>
        </Link>
      </div>
      <ProfileFormErrors
        fieldsOrder={fieldsOrder}
        validationErrors={validationErrors}
        firebaseError={firebaseError}
        className="medium my-0_5"
      />
      <div className="d-flex flex-column align-items-center">
        <ReCaptchaButtonAsync
          action={isSignInAction ? 'SIGN_IN' : 'SIGN_UP'}
          id="smart-sign-submit"
          isActionProgress={isSpinnerActive}
          handleAction={handleSignAction}
          className={classnames('mb-1 w-50', { 'spinner-active': isSpinnerActive })}
          btnText={btnText}
          btnActionText={btnText}
        />
        {!!firebaseError && (
          <Button color="info" disabled={isSpinnerActive} className="w-50" onClick={backAction}>
            Back
          </Button>
        )}
      </div>
    </form>
  );
}

SignPasswordFormUI.propTypes = {
  profileEmail: PropTypes.string.isRequired,
  isVerifyEmail: PropTypes.bool.isRequired,
  fieldRef: PropTypes.func.isRequired,
  fields: PropTypes.objectOf(
    PropTypes.shape({
      password: PropTypes.object,
    })
  ).isRequired,
  fieldsOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
  validate: PropTypes.func.isRequired,
  validationErrors: PropTypes.objectOf(PropTypes.string).isRequired,
  parseFirebaseError: PropTypes.func.isRequired,
  firebaseError: PropTypes.shape({}),
  isAnonymousUser: PropTypes.bool.isRequired,
  dataIdm: PropTypes.shape({}).isRequired,
  createOrUpdateIdmData: PropTypes.func.isRequired,
  updateIdmDataOnLogin: PropTypes.func.isRequired,
  closeAuthenticatedModal: PropTypes.func.isRequired,
  toggleForgotPassword: PropTypes.func.isRequired,
  signCompleted: PropTypes.func.isRequired,
  backAction: PropTypes.func.isRequired,
  isSignInAction: PropTypes.bool,
  disableRecaptcha: PropTypes.bool,
};

export const SignPasswordForm = formValidation(firebaseValidation(SignPasswordFormUI), validators);
